import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft,
  ClipboardCheck,
  Star,
  PenTool,
  CheckCircle2,
  Calendar,
  AlertTriangle,
} from "lucide-react";

const STATUS_TONE = {
  present: "bg-emerald-100 text-emerald-800",
  missing: "bg-red-100 text-red-800",
  damaged: "bg-amber-100 text-amber-800",
  na: "bg-slate-100 text-slate-600",
};

export default function DriverHandovers() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [doc, setDoc] = useState(null);
  const [sigName, setSigName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/vehicle-ops/handovers/driver/my");
      setRows(data || []);
    } catch (e) {
      toast.error("Failed to load handovers");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openHO = async (h) => {
    setActive(h);
    setDoc(null);
    setSigName("");
    setRemarks("");
    try {
      const { data } = await api.get(`/vehicle-ops/handovers/${h.id}`);
      setDoc(data);
    } catch (e) {
      toast.error("Failed to load handover details");
    }
  };

  const acknowledge = async () => {
    if (sigName.trim().length < 2) {
      toast.error("Please type your full name to e-sign");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/vehicle-ops/handovers/driver/${active.id}/ack`, {
        signature_name: sigName.trim(),
        remarks: remarks || null,
      });
      toast.success("Acknowledged — thank you");
      setActive(null);
      setDoc(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  /* -------- Detail view -------- */
  if (active && doc) {
    const pending = doc.status === "pending_driver_ack";
    return (
      <div className="space-y-4 pb-32" data-testid="driver-handover-detail">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setActive(null);
              setDoc(null);
            }}
            className="p-2 -ml-2"
            data-testid="driver-ho-back"
          >
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              {doc.handover_type === "assignment"
                ? "Fresh Assignment"
                : "Monthly Re-verify"}
            </div>
            <h1
              className="text-xl font-bold text-slate-900"
              style={{ fontFamily: "Chivo" }}
            >
              {doc.truck_no}
            </h1>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Handover on {doc.handover_date} · Manager{" "}
              {doc.manager_signature_name}
            </p>
          </div>
        </div>

        {(doc.missing_critical || []).length > 0 && (
          <div className="rounded-xl bg-red-50 ring-1 ring-red-200 p-3 text-xs text-red-900">
            <AlertTriangle className="h-4 w-4 inline mr-1" />
            <strong>Critical items issue:</strong>{" "}
            {doc.missing_critical.join(", ")}
          </div>
        )}

        {doc.critical_notes && (
          <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900">
            <div className="font-semibold mb-1">Manager&apos;s notes</div>
            {doc.critical_notes}
          </div>
        )}

        {doc.walkthrough_video_url && (
          <div>
            <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">
              Outer-Body Walkthrough
            </div>
            <video
              controls
              src={doc.walkthrough_video_url}
              className="rounded-xl w-full max-h-72 bg-black"
            />
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase text-slate-500 font-semibold mb-2">
            Checklist ({doc.items?.length || 0})
          </div>
          <div className="space-y-1.5">
            {(doc.items || []).map((it, i) => (
              <div
                key={i}
                className={`rounded-xl ring-1 p-3 ${
                  it.critical
                    ? "ring-amber-200 bg-amber-50/50"
                    : "ring-slate-200 bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    {it.critical && (
                      <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                    )}
                    {it.label}
                  </div>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${
                      STATUS_TONE[it.status] || "bg-slate-100"
                    }`}
                  >
                    {it.status}
                  </span>
                </div>
                {it.notes && (
                  <div className="text-[11px] text-slate-600 mt-1">
                    {it.notes}
                  </div>
                )}
                {it.photo_url && (
                  <img
                    src={it.photo_url}
                    alt=""
                    className="mt-2 rounded-lg max-h-40 w-full object-cover ring-1 ring-slate-200"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Acknowledgement section */}
        {pending ? (
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <div className="text-[10px] uppercase text-slate-500 font-semibold">
              E-Sign / Acknowledge
            </div>
            <label className="block">
              <span className="text-[11px] text-slate-600">
                Type your full name as signature *
              </span>
              <input
                value={sigName}
                onChange={(e) => setSigName(e.target.value)}
                placeholder="Your full name"
                className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm"
                data-testid="driver-ho-signature"
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-600">
                Remarks (optional)
              </span>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={2}
                placeholder="Any issues you noticed?"
                className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm"
                data-testid="driver-ho-remarks"
              />
            </label>
            <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 z-40">
              <button
                disabled={busy}
                onClick={acknowledge}
                data-testid="driver-ho-ack"
                className="w-full rounded-xl bg-slate-900 text-white py-3 font-bold disabled:bg-slate-400 flex items-center justify-center gap-2"
              >
                <PenTool className="h-4 w-4" />
                {busy ? "Submitting…" : "Acknowledge & Sign"}
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-emerald-50 ring-1 ring-emerald-200 p-3 text-xs text-emerald-900 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Acknowledged by{" "}
            <strong>{doc.driver_signature_name}</strong> on{" "}
            {doc.driver_acknowledged_at?.slice(0, 10)}
          </div>
        )}
      </div>
    );
  }

  /* -------- List view -------- */
  const pendingCount = rows.filter(
    (r) => r.status === "pending_driver_ack",
  ).length;
  return (
    <div className="space-y-4" data-testid="driver-handovers-list">
      <div className="flex items-center gap-2">
        <button onClick={() => nav("/driver")} className="p-2 -ml-2">
          <ChevronLeft className="h-5 w-5 text-slate-600" />
        </button>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            Driver Portal
          </div>
          <h1
            className="text-xl font-bold text-slate-900"
            style={{ fontFamily: "Chivo" }}
          >
            Vehicle Handovers
          </h1>
        </div>
      </div>

      {pendingCount > 0 && (
        <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 p-3 text-xs text-amber-900">
          <AlertTriangle className="h-4 w-4 inline mr-1" />
          <strong>{pendingCount} pending</strong> — please review and
          acknowledge.
        </div>
      )}

      <div className="space-y-2">
        {rows.length === 0 ? (
          <div className="rounded-xl bg-white ring-1 ring-slate-200 p-6 text-center text-sm text-slate-500">
            No handovers yet.
          </div>
        ) : (
          rows.map((h) => (
            <button
              key={h.id}
              onClick={() => openHO(h)}
              data-testid={`driver-ho-row-${h.id}`}
              className="w-full rounded-2xl bg-white ring-1 ring-slate-200 p-4 text-left active:scale-[0.99] transition flex items-center gap-3"
            >
              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <ClipboardCheck className="h-6 w-6 text-slate-700" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-900">
                  {h.truck_no} ·{" "}
                  {h.handover_type === "assignment"
                    ? "Assignment"
                    : "Monthly"}
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> {h.handover_date}
                </div>
                {(h.missing_critical || []).length > 0 && (
                  <div className="text-[10px] text-red-700 mt-0.5">
                    ⚠ {h.missing_critical.length} critical missing
                  </div>
                )}
              </div>
              {h.status === "pending_driver_ack" ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                  ACTION NEEDED
                </span>
              ) : (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
